import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workspacePermissionsApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { UpsertWorkspacePermissionInput } from '../types/api.js';

/**
 * Workspace 権限と管理者の一覧取得フック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business Workspace 権限一覧取得フック。TanStack Query でキャッシュ管理
 * @graph-connects workspacePermissionsApi [calls] API クライアント経由で取得
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
export function useWorkspacePermissions() {
  return useQuery({
    queryKey: queryKeys.workspacePermissions.current(),
    queryFn: workspacePermissionsApi.get,
    // 権限は頻繁に変わらないので 30 秒 stale 許容（pipeline 権限画面と揃える）
    staleTime: 30_000,
  });
}

/**
 * Workspace 権限の登録・更新フック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business Workspace 権限保存ミューテーション。保存後にキャッシュを invalidate
 * @graph-connects workspacePermissionsApi [calls] API クライアント経由で保存
 * @graph-connects queryKeys [calls] TanStack Query キャッシュ無効化
 */
export function useUpsertWorkspacePermission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertWorkspacePermissionInput) => workspacePermissionsApi.upsert(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspacePermissions.all });
    },
  });
}

/**
 * Workspace 権限の失効フック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business Workspace 権限失効ミューテーション。失効後にキャッシュを invalidate
 * @graph-connects workspacePermissionsApi [calls] API クライアント経由で失効
 * @graph-connects queryKeys [calls] TanStack Query キャッシュ無効化
 */
export function useRevokeWorkspacePermission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => workspacePermissionsApi.revoke(email),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspacePermissions.all });
    },
  });
}

/**
 * Workspace 権限の管理者保存フック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business Workspace 権限管理者の保存ミューテーション。保存後にキャッシュを invalidate
 * @graph-connects workspacePermissionsApi [calls] API クライアント経由で保存
 * @graph-connects queryKeys [calls] TanStack Query キャッシュ無効化
 */
export function useSaveWorkspacePermissionAdmins() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (admins: string[]) => workspacePermissionsApi.saveAdmins(admins),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspacePermissions.all });
    },
  });
}
